import { EyeIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EvidenceFocus } from "../../../annotation/textEvidence";
import { AskInChatButton, SelectionToChat } from "../../../chatReferences";
import type { ShownStatement, StatementCardShape } from "../../../knowledgeData/abox/aboxViewModel";
import type { TermCards } from "../../../ontology";
import StatementSummary from "../../../knowledgeData/abox/StatementSummary";
import IconButton from "../../../../ui/buttons/IconButton";
import { classNames } from "../../../../ui/styles/classNames";
import { itemKeys, type PlacedStatement } from "./regestParts";

interface PartStatementsProps {
  /** The part's statements, each laid out, with those it shows by a value (`absorbNamedIndividuals`). */
  readonly items: readonly ShownStatement<PlacedStatement>[];
  /** The terms of the statements' project, by model version: `null` for one that could not be read. */
  readonly cards: ReadonlyMap<string, TermCards | null>;
  readonly focus: EvidenceFocus;
  /** Where selecting a statement's words asks the agent about its result (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What a statement's line also shows. */
  readonly extra: ((statement: PlacedStatement) => ReactNode) | undefined;
  /** Each line's number in reading order, by its statement's key, which its words in the text also carry. */
  readonly numbers: ReadonlyMap<string, number>;
  /** How the project's profile reads one statement (Daniel's card); `null` shows it as written. */
  readonly shape: StatementCardShape | null;
}

/**
 * What the data says about one part of the text, beside it (O-1): each statement once, compact.
 * A statement not accepted, such as a proposal under review, is drawn apart from accepted ones
 * (F154). Pointing at one highlights its words in the text; its button, or a click on the line,
 * keeps them highlighted, and a kept statement offers to ask about its result (F96).
 * `data-statements` lets a click on the words find it again.
 */
export default function PartStatements({ items, cards, focus, askInProject, extra, numbers, shape }: PartStatementsProps) {
  const { t } = useTranslation();
  // Lines that are only evidence, such as the entry's structure, follow under one fold, after what is said (usability
  // round 3, 4 Nov: no line of its own named by class names).
  const evidenceOnly = items.filter((item) => item.layout.said.length === 0);
  // A line chosen from its words in the text opens the fold that holds it, so the other side is in view (O-1; Sol R7).
  const evidenceFold = useRef<HTMLDetailsElement>(null);
  const chosenInFold = evidenceOnly.some((item) => itemKeys(item).some((key) => focus.selected.has(key)));
  useEffect(() => {
    if (chosenInFold && evidenceFold.current !== null) evidenceFold.current.open = true;
  }, [chosenInFold]);
  const lines = (shown: typeof items) => shown.map((item) => {
        const keys = itemKeys(item);
        const highlighted = keys.some((key) => focus.highlighted.has(key));
        const kept = keys.some((key) => focus.selected.has(key));
        const summary = <StatementSummary layout={item.layout} note={item.shown.statement.note} modelCommit={item.shown.modelCommit} cards={cards.get(item.shown.modelCommit) ?? null} number={numbers.get(item.shown.key) ?? null} shape={shape} />;
        const subject = { reference: item.shown.source, name: t("chatReferences.statements") };
        // A click on the line keeps it; one on a term, an action or the evidence's fold inside it does only that.
        const keepOnClick = (event: MouseEvent) => {
          if (event.target instanceof Element && event.target.closest("a, button, summary") !== null) return;
          focus.onSelect(keys);
        };
        return (
          <li
            key={item.shown.key}
            data-statements={keys.join(" ")}
            onMouseEnter={() => { focus.onPoint(keys); }}
            onMouseLeave={() => { focus.onPoint(null); }}
            onClick={keepOnClick}
            className={classNames("flex cursor-pointer items-start gap-2 rounded px-2 py-1", !item.shown.accepted && "app-statement-proposed", highlighted && "app-evidence-focused")}
          >
            <div className="min-w-0 flex-1 space-y-1">
              {item.shown.accepted ? null : <span className="sr-only">{t("sources.document.regest.proposed")}</span>}
              {askInProject === undefined ? summary : <SelectionToChat projectId={askInProject} subject={subject}>{summary}</SelectionToChat>}
              {extra?.(item.shown)}
              {kept && askInProject !== undefined ? <AskInChatButton projectId={askInProject} subject={subject} /> : null}
            </div>
            <IconButton
              label={t("sources.document.added.showInText")}
              title={t("sources.document.added.showInText")}
              aria-pressed={kept}
              onClick={() => { focus.onSelect(keys); }}
              onFocus={() => { focus.onPoint(keys); }}
              onBlur={() => { focus.onPoint(null); }}
              className="h-7 w-7 shrink-0"
            >
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </li>
        );
      });
  return (
    <ul className="space-y-1 text-sm">
      {lines(items.filter((item) => item.layout.said.length > 0))}
      {evidenceOnly.length === 0 ? null : (
        <li>
          <details ref={evidenceFold} className="space-y-1">
            <summary className="app-text-muted w-fit cursor-pointer px-2 text-xs">{t("data.abox.evidence")}</summary>
            <ul className="space-y-1">{lines(evidenceOnly)}</ul>
          </details>
        </li>
      )}
    </ul>
  );
}
